
import { useEffect, useRef, useState } from "react";
import ProtectedVideo from "./ProtectedVideo";

const playbackRates = [0.5, 0.75, 1, 1.25, 1.5, 2];

export const VideoPlayer = ({ video, onClose }) => {
  const [isMaximized, setIsMaximized] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenError, setFullscreenError] = useState("");
  const [playbackRate, setPlaybackRate] = useState(1);
  const [playerError, setPlayerError] = useState(false);
  const videoRef = useRef(null);
  const videoContainerRef = useRef(null);


  // Dynamic detection matching existing VideoPlayerPopup: embed links (e.g. player.mediadelivery.net) need an iframe, normal files use <video>
  const rawUrl = video?.videoUrl || video?.videoPath || "";
  const isEmbed = rawUrl.includes("player.mediadelivery.net") || rawUrl.includes("embed") || !/\.(mp4|webm|ogg|mov)($|\?)/i.test(rawUrl);
  const videoUrl = isEmbed ? rawUrl.replace("/play/", "/embed/") : rawUrl;

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === videoContainerRef.current);
      setFullscreenError("");
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  useEffect(() => {
    if (!video) return;

    const previousBodyOverflow = document.body.style.overflow;
    const previousRootOverflow = document.documentElement.style.overflow;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        if (document.fullscreenElement === videoContainerRef.current) {
          document.exitFullscreen().catch((error) => {
            console.error("Unable to exit video fullscreen mode.", error);
          });
          return;
        }
        onClose();
        return;
      }

      if (
        (event.key !== "ArrowLeft" && event.key !== "ArrowRight") ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey
      ) {
        return;
      }

      const target = event.target;
      if (
        target instanceof HTMLElement &&
        target.closest("button, select, input, textarea, a, [contenteditable='true']")
      ) {
        return;
      }

      const player = videoRef.current;
      if (!player) return;

      event.preventDefault();
      const duration = Number.isFinite(player.duration) ? player.duration : Infinity;
      const offset = event.key === "ArrowRight" ? 10 : -10;
      player.currentTime = Math.min(
        duration,
        Math.max(0, player.currentTime + offset)
      );
    };

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousRootOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [video, onClose]);

  useEffect(() => {
    setPlaybackRate(1);
    setPlayerError(false);
  }, [videoUrl]);

  const seekVideo = (offsetSeconds) => {
    const player = videoRef.current;
    if (!player) return;

    const duration = Number.isFinite(player.duration) ? player.duration : Infinity;
    player.currentTime = Math.min(
      duration,
      Math.max(0, player.currentTime + offsetSeconds)
    );
  };

  const changePlaybackRate = (event) => {
    const rate = Number(event.target.value);
    setPlaybackRate(rate);
    if (videoRef.current) {
      videoRef.current.playbackRate = rate;
    }
  };

  const toggleFullscreen = async () => {
    const videoContainer = videoContainerRef.current;
    if (!videoContainer) return;

    setFullscreenError("");
    try {
      if (document.fullscreenElement === videoContainer) {
        await document.exitFullscreen();
      } else if (videoContainer.requestFullscreen) {
        await videoContainer.requestFullscreen();
      } else {
        setFullscreenError("Fullscreen is not supported by this browser.");
      }
    } catch (error) {
      console.error("Unable to change video fullscreen mode.", error);
      setFullscreenError("Unable to enter fullscreen. Please try again.");
    }
  };

  if (!video) return null;

  return (
    <div
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100005,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: isMaximized ? 0 : "16px",
        background: "rgba(0,0,0,.90)",
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-label={video.title || ""}
        style={{
          width: isMaximized ? "100vw" : "min(100%, 1000px)",
          height: isMaximized ? "100dvh" : "auto",
          maxHeight: "100dvh",
          display: isMaximized ? "flex" : "block",
          flexDirection: "column",
          overflow: isMaximized ? "hidden" : "auto",
          background: "#102b3c",
          borderRadius: isMaximized ? 0 : "12px",
          boxShadow: "0 16px 48px rgba(0,0,0,.5)",
          boxSizing: "border-box",
        }}
      >
        {/* Player header */}
        <header
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            padding: "10px 16px",
            color: "#fff",
            flexShrink: 0,
          }}
        >
          <h3
            style={{
              fontSize: 18,
              fontWeight: 400,
              margin: 0,
              overflowWrap: "anywhere",
            }}
          >
            {video.title || ""}
          </h3>

          <div style={{ display: "flex", gap: 8 }}>
            {/* <button
              type="button"
              onClick={() =>
                setIsMaximized((previous) => !previous)
              }
              aria-label={
                isMaximized ? "Restore player" : "Maximize player"
              }
              title={isMaximized ? "Restore" : "Maximize"}
              style={buttonStyle}
            >
              {isMaximized ? "↙" : "↗"}
            </button> */}

            <button
              type="button"
              onClick={onClose}
              aria-label="Close video player"
              title="Close"
              style={buttonStyle}
            >
              ✕
            </button>
          </div>
        </header>

        {/* Responsive video */}
        <div
          ref={videoContainerRef}
          onContextMenu={(event) => event.preventDefault()}
          style={{
            position: "relative",
            width: "100%",
            aspectRatio: isMaximized ? "auto" : "16 / 9",
            flex: isMaximized ? "1 1 0" : "0 0 auto",
            minHeight: isMaximized ? 0 : undefined,
            background: "#000",
            ...(isFullscreen
              ? { width: "100vw", height: "100dvh", aspectRatio: "auto" }
              : {}),
          }}
        >
          {!videoUrl ? (
            <div
              role="alert"
              className="position-absolute top-50 start-50 translate-middle"
              style={{
                color: "#fff",
                padding: 24,
                textAlign: "center",
                width: "100%",
              }}
            >
              No video URL available for this session.
            </div>
          )
            :
            isEmbed ?
              (
                <iframe
                  key={videoUrl}
                  src={videoUrl}
                  title="Video player"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    border: 0,
                    background: "#000",
                  }}
                />
              ) : !playerError ? (
                <video
                  ref={videoRef}
                  key={videoUrl}
                  src={videoUrl}
                  poster={video?.thumbnailUrl || undefined}
                  title={video.title || "Video"}
                  controls
                  autoPlay
                  playsInline
                  controlsList="nodownload nofullscreen noremoteplayback"
                  disablePictureInPicture
                  onContextMenu={(event) => event.preventDefault()}
                  onError={(e) => {
                    console.error("[VideoPlayer] Native video error:", e, "URL:", videoUrl);
                    setPlayerError(true);
                  }}
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    border: 0,
                    background: "#000",
                  }}
                />
              ) : (
                <div
                  role="alert"
                  className="position-absolute top-50 start-50 translate-middle"
                  style={{
                    color: "#fff",
                    padding: 24,
                    textAlign: "center",
                    width: "100%",
                  }}
                >
                  This video could not be loaded. Please try again later.
                </div>
              )}
          <ProtectedVideo userEmail={video?.userEmail || "Authorized User"} />
        </div>
        

      </section>
    </div>
  );
};

const buttonStyle = {
  color: "#fff",
  background: "transparent",
  border: "1px solid #78909c",
  borderRadius: 6,
  minWidth: 25,
  height: 25,
  cursor: "pointer",
  fontSize: 14,
};

const controlButtonStyle = {
  color: "#fff",
  background: "transparent",
  border: "1px solid #78909c",
  borderRadius: 6,
  padding: "6px 12px",
  cursor: "pointer",
};